import { useState } from 'react'
import { GenNode } from '../types/carbon/models/GenNode'
import type { MoveUserTableResponse, RemoveTablesReference, RemoveTablesResponse } from '../types/gotypes'
import { tocApi } from '../services/tocApi'
import { getNodeID, getNodeTitle } from '../utils/nodeTypes'
import { extractErrorMessage } from '../utils/apitools'
import type { CheckboxState } from '../utils/tree'
import useSnackbar from './useSnackbar'

// Deleting tables and folders from the Tables tree, and renaming a My Tables
// folder (#1795). The server resolves a folder to every table under it at any
// depth, so a count here is the server's, never node.children.length.
// Ticking in Select mode and the folder's own Delete share one path: a dry
// run, a confirm that shows the count, then the real call.

/** Every delete, of one table or of thousands, asks for this word (Adam, 13 Oct). */
const CONFIRM_WORD = 'delete'

/** Tables per delete request after the confirm, so the page can count them
 *  ("Deleting 40 of 171 tables") instead of looking frozen (#1809). */
export const REMOVE_PART_SIZE = 25

interface PendingRemoval {
  title: string
  message: string
  confirmWord: string
  folders: string[]
  /** The dry run's list of every table that goes, in delete order. */
  planned: string[]
}

/** Progress of a confirmed delete: tables deleted (or refused) so far. */
export interface RemovalProgress {
  done: number
  total: number
}

const tablePath = (node: GenNode) => `${getNodeID(node)}.cbt`
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

/**
 * The server's reason for refusing a rename or a move. A 409 for tables in
 * use names what uses them, so the user knows what to change.
 */
const refusal = (err: unknown) => {
  const refs: RemoveTablesReference[] = (err as { response?: { data?: { referenced?: RemoveTablesReference[] } } })?.response?.data?.referenced ?? []
  const users = [...new Set(refs.map(r => r.by))]
  const usedBy = users.length ? `. Used by ${users.slice(0, 3).join(', ')}${users.length > 3 ? ` and ${users.length - 3} more` : ''}.` : ''
  return `${extractErrorMessage(err)}${usedBy}`
}

/** Whether a Table or Folder node can be ticked at all. */
const isTickable = (node: GenNode) => node.type === 'Table' || node.type === 'Folder'

/** The delete action's answer for a node: disabled with a reason, or allowed. */
export interface DeleteDecision {
  disabled?: boolean
  reason?: string
}

export const useTableRemoval = (
  customer: string | undefined,
  job: string | undefined,
  /** Called after the server deleted tables, with their blob paths (.cbt). */
  onRemoved: (deletedPaths: string[]) => Promise<void> | void,
) => {
  const snackbar = useSnackbar()
  const [selectMode, setSelectMode] = useState(false)
  const [ticked, setTicked] = useState<Record<string, GenNode>>({})
  const [pending, setPending] = useState<PendingRemoval | null>(null)
  const [busy, setBusy] = useState(false)
  const [progress, setProgress] = useState<RemovalProgress | null>(null)

  // A ticked folder stands for everything in it: its rows show ticked too.
  const tickedFolderIds = Object.keys(ticked).filter(id => ticked[id].type === 'Folder')
  const isCovered = (id: string) => tickedFolderIds.some(f => id.startsWith(`${f}/`))

  const handleToggleSelectMode = () => {
    setSelectMode(on => !on)
    setTicked({})
  }

  const getCheckboxState = (node: GenNode, decide: (node: GenNode) => DeleteDecision | undefined): CheckboxState => {
    if (!selectMode || !isTickable(node)) return { enabled: false, checked: false }
    const id = getNodeID(node)
    const decision = decide(node)
    if (!decision) return { enabled: false, checked: false }
    if (decision.disabled) return { enabled: false, checked: false, reason: decision.reason }
    return { enabled: true, checked: id in ticked || isCovered(id) }
  }

  const handleNodeCheckedToggle = (_itemId: string, node: GenNode, isChecked: boolean) => {
    const id = getNodeID(node)
    if (isCovered(id)) return
    setTicked(prev => {
      const next = { ...prev }
      if (isChecked) next[id] = node
      else delete next[id]
      return next
    })
  }

  // What the ticks ask for: tables and folders, with anything inside a ticked
  // folder left out (the folder already covers it).
  const requestFor = (nodes: GenNode[]) => {
    const folderIds = nodes.filter(n => n.type === 'Folder').map(getNodeID)
    const inFolder = (id: string) => folderIds.some(f => id.startsWith(`${f}/`))
    const folders = folderIds.filter(id => !inFolder(id))
    const paths = nodes.filter(n => n.type === 'Table' && !inFolder(getNodeID(n))).map(tablePath)
    return { paths, folders }
  }

  const describe = (nodes: GenNode[], plan: RemoveTablesResponse): { title: string; message: string } => {
    const folderNodes = nodes.filter(n => n.type === 'Folder')
    const referenced = new Set(plan.referenced.map(r => r.path)).size
    const used = referenced > 0
      ? ` ${plural(referenced, 'table')} ${referenced === 1 ? 'is' : 'are'} used by dashboards or other outputs, which will stop working.`
      : ''
    let message: string
    let title = 'Delete Tables'
    if (nodes.length === 1 && folderNodes.length === 1) {
      const name = getNodeTitle(folderNodes[0])
      title = 'Delete Folder'
      message = plan.count === 0
        ? `Delete empty folder "${name}"?`
        : `Delete folder "${name}" and the ${plural(plan.count, 'table')} inside it (including subfolders)?`
    } else if (folderNodes.length === 0) {
      message = `Delete ${plural(plan.count, 'table')}?`
    } else {
      message = `Delete ${plural(nodes.length, 'selected item')}, ${plural(plan.count, 'table')} in all (including those inside folders)?`
    }
    return { title, message: `${message}${used} This cannot be undone.` }
  }

  /** Dry run, then open the confirm. Nothing is deleted until handleConfirm. */
  const handleRequestDelete = async (nodes: GenNode[]) => {
    if (!customer || !job || nodes.length === 0) return
    const { paths, folders } = requestFor(nodes)
    setBusy(true)
    try {
      const plan = await tocApi.removeTables(customer, job, { paths, folders, dryRun: true })
      const { title, message } = describe(nodes, plan)
      setPending({ title, message, confirmWord: CONFIRM_WORD, folders, planned: plan.deleted })
    } catch (err) {
      snackbar.error(`Failed to delete: ${extractErrorMessage(err)}`)
    } finally {
      setBusy(false)
    }
  }

  // A row ticked before its folder is covered by the folder: count it once.
  const tickedNodes = Object.entries(ticked).filter(([id]) => !isCovered(id)).map(([, node]) => node)

  const handleDeleteTicked = () => handleRequestDelete(tickedNodes)

  const handleCancel = () => setPending(null)

  /**
   * Deletes what the dry run planned, REMOVE_PART_SIZE tables a request, one
   * after another, counting up. Then the folders themselves go in one last
   * request: the server finds them empty (or deletes what appeared since the
   * dry run) and drops their tree entries. A failed request stops the run;
   * what was deleted is reported and the tree reloads.
   */
  const handleConfirm = async () => {
    if (!customer || !job || !pending) return
    const { folders, planned } = pending
    setPending(null)
    setBusy(true)
    const total = planned.length
    const deleted: string[] = []
    const failed: { path: string; error: string }[] = []
    let stopped: unknown = null
    setProgress({ done: 0, total })
    try {
      for (let start = 0; start < total; start += REMOVE_PART_SIZE) {
        const part = planned.slice(start, start + REMOVE_PART_SIZE)
        const res = await tocApi.removeTables(customer, job, { paths: part, folders: [], dryRun: false })
        deleted.push(...res.deleted)
        failed.push(...res.failed)
        setProgress({ done: Math.min(start + part.length, total), total })
      }
      if (folders.length > 0) {
        const res = await tocApi.removeTables(customer, job, { paths: [], folders, dryRun: false })
        deleted.push(...res.deleted)
        failed.push(...res.failed)
      }
    } catch (err) {
      stopped = err
    }
    try {
      setTicked({})
      if (deleted.length > 0) await onRemoved(deleted)
      if (stopped) {
        snackbar.error(`Deleted ${deleted.length} of ${plural(total, 'table')}, then stopped: ${extractErrorMessage(stopped)}`)
      } else if (failed.length > 0) {
        snackbar.warning(`Deleted ${plural(deleted.length, 'table')}; ${failed.length} could not be deleted: ${failed[0].error}`)
      } else {
        snackbar.success(`Deleted ${plural(deleted.length, 'table')}`)
      }
    } finally {
      setProgress(null)
      setBusy(false)
    }
  }

  /**
   * Rename a My Tables folder. The server moves every table in it. A dry run
   * first surfaces a clash or a table in use (409) before anything moves.
   * Throws after showing the server's reason, so the name dialog stays open.
   */
  const handleRenameUserFolder = async (node: GenNode, newName: string) => {
    if (!customer || !job) return undefined
    const folder = getNodeID(node)
    try {
      await tocApi.renameUserFolder(customer, job, { folder, newName, dryRun: true })
      const res = await tocApi.renameUserFolder(customer, job, { folder, newName, dryRun: false })
      // Every table was copied; an old copy the server could not remove
      // shows at the top of My Tables until it is deleted.
      if (res.failed?.length) {
        snackbar.warning(`Renamed the folder, but ${plural(res.failed.length, 'old table')} could not be removed and will show at the top of My Tables: ${res.failed[0].error}`)
      }
      return res
    } catch (err) {
      snackbar.error(`Failed to rename: ${refusal(err)}`)
      throw err
    }
  }

  /**
   * Move a My Tables table into another My Tables folder ('' = the top
   * level), and rename it when newName is set. The server moves the blob, so
   * the tree shows the table where it now is. A dry run first surfaces a
   * clash or a table in use (409) before anything moves. Throws after
   * showing the server's reason.
   */
  const handleMoveUserTable = async (node: GenNode, destFolder: string, newName?: string): Promise<MoveUserTableResponse | undefined> => {
    if (!customer || !job) return undefined
    const body = { path: tablePath(node), destFolder, ...(newName ? { newName } : {}) }
    try {
      await tocApi.moveUserTable(customer, job, { ...body, dryRun: true })
      const res = await tocApi.moveUserTable(customer, job, { ...body, dryRun: false })
      // The table was copied; an old copy the server could not remove shows
      // at the top of My Tables until it is deleted.
      if (res.failed?.length) {
        snackbar.warning(`${newName ? 'Renamed' : 'Moved'} the table, but the old copy could not be removed and will show at the top of My Tables: ${res.failed[0].error}`)
      }
      return res
    } catch (err) {
      snackbar.error(`Failed to ${newName ? 'rename' : 'move'}: ${refusal(err)}`)
      throw err
    }
  }

  return {
    selectMode,
    selectedCount: tickedNodes.length,
    pending,
    busy,
    progress,
    getCheckboxState,
    handleNodeCheckedToggle,
    handleToggleSelectMode,
    handleRequestDelete,
    handleDeleteTicked,
    handleConfirm,
    handleCancel,
    handleRenameUserFolder,
    handleMoveUserTable,
  }
}

export default useTableRemoval
